<template>
  <div class="form-section">
    <h3 class="text-responsive-base font-bold text-center mb-responsive text-gray-800">
      Request Policy Changes
    </h3>
    <p class="text-center text-sm text-gray-600 mb-6">
      Select the changes you'd like to make to your auto insurance policy.
    </p>
    
    <!-- Error message if no changes selected -->
    <div v-if="errors.changes" class="bg-red-50 border-l-4 border-red-500 p-3 mb-4">
      <p class="text-red-700 text-sm">{{ errors.changes }}</p>
    </div>
    
    <!-- Change Type Buttons -->
    <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
      <button 
        type="button" 
        @click="addChangeType('addVehicle')" 
        class="change-action-button"
      >
        + Add Vehicle
      </button>
      <button 
        type="button" 
        @click="addChangeType('removeVehicle')" 
        class="change-action-button"
      >
        - Remove Vehicle
      </button>
      <button 
        type="button" 
        @click="addChangeType('replaceVehicle')" 
        class="change-action-button"
      >
        Replace Vehicle
      </button>
      <button 
        type="button" 
        @click="addChangeType('addDriver')" 
        class="change-action-button"
      >
        + Add Driver
      </button>
      <button 
        type="button" 
        @click="addChangeType('removeDriver')" 
        class="change-action-button"
      >
        - Remove Driver
      </button>
      <button 
        type="button" 
        @click="addChangeType('updateAddress')" 
        class="change-action-button"
      >
        Update Address
      </button>
      <button 
        type="button" 
        @click="addChangeType('updateCoverages')" 
        class="change-action-button"
      >
        Update Coverages
      </button>
      <button 
        type="button" 
        @click="addChangeType('updateLienholder')" 
        class="change-action-button"
      >
        Update Lienholder
      </button>
      <button 
        type="button" 
        @click="addChangeType('other')" 
        class="change-action-button"
      >
        Other Request
      </button>
    </div>
    
    <!-- No Changes Selected Message -->
    <div v-if="localActiveChanges.length === 0" class="text-center text-gray-500 py-8 border border-dashed border-gray-300 rounded-md mb-6">
      <Icon icon="mdi:car-cog" class="text-gray-400 text-4xl mb-2" />
      <p>Please select a change type above to add details.</p>
    </div>
    
    <!-- Dynamically Rendered Change Sections -->
    <div v-else class="space-y-4 mb-6">
      <div 
        v-for="(change, index) in localActiveChanges" 
        :key="index" 
        class="border p-4 rounded-md relative bg-gray-50"
      >
        <div class="flex justify-between items-center mb-3">
          <h4 class="font-semibold text-ietextlight">{{ getChangeTypeTitle(change.type) }}</h4>
          <button 
            type="button" 
            @click="removeChangeType(index)" 
            class="text-red-500 hover:text-red-700"
            aria-label="Remove this change"
          >
            <Icon icon="mdi:close-circle" class="h-6 w-6" />
          </button>
        </div>
        
        <!-- Dynamic Component Rendering -->
        <component 
          v-if="getComponentForChangeType(change.type)"
          :is="getComponentForChangeType(change.type)"
          v-model="change.details"
          :ref="el => { if (el) componentRefs[index] = el }" 
        />
        <div v-else>
          <p class="text-red-500">Error: Component not found for change type '{{ change.type }}'.</p>
        </div>
      </div>
    </div>
    
    <!-- Navigation Buttons -->
    <div class="flex justify-between">
      <button
        @click="$emit('previous')"
        class="btn-responsive-secondary"
        type="button"
      >
        Back
      </button>
      <button
        @click="handleNext"
        :class="customerType === 'lender' ? 'btn-lender' : 'btn-customer'"
        type="button"
      >
        Next
      </button>
    </div>
  </div>
</template>

<script>
import { ref, reactive, watch } from 'vue';
import { Icon as IconifyIcon } from '@iconify/vue';

// Import the section components
import AddVehicleSection from '@/components/customerservice/policy-management/forms/auto/steps/sections/AddVehicleSection.vue';
import RemoveVehicleSection from '@/components/customerservice/policy-management/forms/auto/steps/sections/RemoveVehicleSection.vue';
import ReplaceVehicleSection from '@/components/customerservice/policy-management/forms/auto/steps/sections/ReplaceVehicleSection.vue';
import AddDriverSection from '@/components/customerservice/policy-management/forms/auto/steps/sections/AddDriverSection.vue';
import RemoveDriverSection from '@/components/customerservice/policy-management/forms/auto/steps/sections/RemoveDriverSection.vue';
import UpdateAddressSection from '@/components/customerservice/policy-management/forms/auto/steps/sections/UpdateAddressSection.vue';
import UpdateCoveragesSection from '@/components/customerservice/policy-management/forms/auto/steps/sections/UpdateCoveragesSection.vue';
import UpdateLienholderSection from '@/components/customerservice/policy-management/forms/auto/steps/sections/UpdateLienholderSection.vue';
import OtherRequestSection from '@/components/customerservice/policy-management/forms/auto/steps/sections/OtherRequestSection.vue';

export default {
  name: 'PolicyChangesStep',
  components: {
    Icon: IconifyIcon,
    AddVehicleSection,
    RemoveVehicleSection,
    ReplaceVehicleSection,
    AddDriverSection,
    RemoveDriverSection,
    UpdateAddressSection,
    UpdateCoveragesSection,
    UpdateLienholderSection,
    OtherRequestSection
  },
  props: {
    activeChanges: {
      type: Array,
      default: () => []
    },
    customerType: {
      type: String,
      default: 'customer'
    },
    errors: {
      type: Object,
      default: () => ({})
    }
  },
  emits: [
    'update:activeChanges',
    'previous',
    'next'
  ],
  setup(props, { emit }) {
    // Constants for validation throttling
    const MIN_VALIDATION_INTERVAL = 800; // ms between validations
    const validationInProgress = ref(false);
    const lastValidationTime = ref(0);

    // Local copy of active changes for v-model
    const localActiveChanges = ref([...props.activeChanges]);
    
    // Watch for changes in props.activeChanges and update local copy
    watch(() => props.activeChanges, (newValue) => {
      localActiveChanges.value = [...newValue];
    });
    
    // Watch for changes in localActiveChanges and emit updates
    // Add throttling to this watch to prevent excessive updates
    let updateTimeout = null;
    watch(localActiveChanges, (newValue) => {
      if (updateTimeout) clearTimeout(updateTimeout);
      updateTimeout = setTimeout(() => {
        emit('update:activeChanges', newValue);
      }, 500); // Throttle updates to 500ms
    }, { deep: true });
    
    // Component refs for validation
    const componentRefs = reactive({});
    
    // Get change type title
    const getChangeTypeTitle = (type) => {
      const titles = {
        addVehicle: 'Add Vehicle Details',
        removeVehicle: 'Vehicle to Remove',
        replaceVehicle: 'Replace Vehicle Details',
        addDriver: 'Add Driver Details',
        removeDriver: 'Driver to Remove',
        updateAddress: 'Update Address Details',
        updateCoverages: 'Update Coverage Details',
        updateLienholder: 'Update Lienholder Details',
        other: 'Other Request Details'
      };
      return titles[type] || 'Unknown Change';
    };
    
    // Get component for change type
    const getComponentForChangeType = (type) => {
      const map = {
        addVehicle: AddVehicleSection,
        removeVehicle: RemoveVehicleSection,
        replaceVehicle: ReplaceVehicleSection,
        addDriver: AddDriverSection,
        removeDriver: RemoveDriverSection,
        updateAddress: UpdateAddressSection,
        updateCoverages: UpdateCoveragesSection,
        updateLienholder: UpdateLienholderSection,
        other: OtherRequestSection,
      };
      return map[type] || null;
    };
    
    // Add change type
    const addChangeType = (type) => {
      localActiveChanges.value.push({ type, details: {} });
    };
    
    // Remove change type
    const removeChangeType = (index) => {
      localActiveChanges.value.splice(index, 1);
    };
    
    // Validate all section components
    const validate = () => {
      // Prevent validation if one is in progress or if called too frequently
      const now = Date.now();
      if (validationInProgress.value || (now - lastValidationTime.value < MIN_VALIDATION_INTERVAL)) {
        console.log('[PolicyChangesStep] Validation throttled - skipping to prevent loops');
        return true; // Return success to break potential loops
      }
      
      validationInProgress.value = true;
      lastValidationTime.value = now;
      
      try {
        // If no changes selected, validation happens in parent
        if (localActiveChanges.value.length === 0) {
          validationInProgress.value = false;
          return false;
        }
        
        // Check each component synchronously
        let allValid = true;
        Object.keys(componentRefs).forEach(key => {
          const component = componentRefs[key];
          if (component && typeof component.validate === 'function') {
            try {
              // Synchronous validation only
              const componentValid = component.validate();
              
              // Only process direct boolean results, not Promises
              if (typeof componentValid === 'boolean') {
                if (!componentValid) allValid = false;
              } else {
                console.warn(`[PolicyChangesStep] Component returned non-boolean validation: ${typeof componentValid}`);
                allValid = false; // Treat non-booleans as failures
              }
            } catch (err) {
              console.error('[PolicyChangesStep] Error validating component:', err);
              allValid = false;
            }
          }
        });
        
        return allValid;
      } catch (error) {
        console.error('[PolicyChangesStep] Validation error:', error);
        return false;
      } finally {
        validationInProgress.value = false;
      }
    };
    
    // Handle next button click
    const handleNext = () => {
      // Prevent validation if one is in progress
      if (validationInProgress.value) {
        console.log('[PolicyChangesStep] Validation in progress - skipping next button validation');
        return;
      }
      
      if (localActiveChanges.value.length === 0) {
        emit('next');
        return;
      }
      
      const isValid = validate();
      if (isValid) {
        emit('next');
      }
    };
    
    return {
      localActiveChanges,
      componentRefs,
      getChangeTypeTitle,
      getComponentForChangeType,
      addChangeType,
      removeChangeType,
      validate,
      handleNext
    };
  }
};
</script>

<style scoped>
.form-section {
  @apply p-4 md:p-6;
}

.change-action-button {
  @apply py-2 px-3 bg-white border border-gray-300 rounded shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors;
}

.btn-responsive-secondary {
  @apply px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-medium transition-colors;
}
</style> 